import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Stack,
  MenuItem,
  TextField,
  useTheme,
  Link,
  Button,
  Tooltip,
} from "@mui/material";
import { useSelector } from "react-redux";

import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getFormattedDate } from "utils/time.util";
import {
  setBoclipsPopup,
  setBoclipsVideoId,
  setCollectionId,
  setSelectedBookmarkOption,
} from "context/features/dataSlice";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { logEvent } from "services/amplitude.service";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import { useSourceSearch } from "features/SourceSearch";
// import getRefreshTOkenWashingtonPost from "hooks/use-washinghton-token";

const Header = ({
  toggleSidebar = null,
  selectedBookmarkOption,
  edit,
  setEdit,
  saveNewCollection,
  setSelectedCollection,
  selectedCollection,
  switchBookmark,
  setDeletePopup,
  create,
  setCreate,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [bookmarkTitle, setBookmarkTitle] = useState("");
  const dispatch = useDispatch();
  const [disabled, setDisabled] = useState(false);

  const theme = useTheme();
  const navbarBackground = theme.palette.secondary.main;

  const {
    summaryInfoData,
    collections,
    collectionSourceId,
    collectionId,
    parentCollections,
  } = useSelector((state) => state.data);

  const { currentCategory, currentSearchTerm } = useSourceSearch();

  const handleSelectBookmarkOption = (option) => {
    dispatch(setSelectedBookmarkOption(option));
    logEvent(`click_${option}_option`, { click: true });
  };

  const handleSelect = async (event) => {
    if (event.target.value === "create_new") {
      dispatch(setCollectionId(null));
      setSelectedCollection(null);
    } else if (create) {
      dispatch(setCollectionId(event.target.value));
      setSelectedCollection(event.target.value);
    } else {
      await switchBookmark(event.target.value);
      toast.success("Bookmark saved to collection successfully!");
    }
  };

  const handleSaveNewCollection = async () => {
    if(disabled) return;
    try {
      if (bookmarkTitle.length === 0) {
        toast.error("Collection name cannot be empty!");
        return;
      }
      setDisabled(true);
      const id = await saveNewCollection(bookmarkTitle);
      dispatch(setCollectionId(id));
      setCreate(false);
      await switchBookmark(id);
      toast.success("Bookmark saved to collection successfully!");      
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setTimeout(() => {
        setDisabled(false);
      }, 1000);
    }
  };

  const TopHeader = () => {
    return (
      <Stack
        direction="row"
        p={2}
        justifyContent="space-between"
        alignItems="center"
      >
        {!edit ? (
          <Typography
            variant="h5"
            gutterBottom
            sx={{ color: "#696b6c", fontSize: "2rem", mb: 0 }}
          >
            Details
          </Typography>
        ) : (
          <Typography
            onClick={() => logEvent("click_edit_bookmark", { click: true })}
            variant="h5"
            gutterBottom
            sx={{ color: "#696b6c", fontSize: "2rem", mb: 0 }}
          >
            Edit Bookmark
          </Typography>
        )}

        <Stack direction="row" spacing={0.5}>
          {collectionSourceId && (
            <>
              {!edit && (
                <IconButton onClick={() => setEdit(true)} color="inherit">
                  <EditOutlinedIcon
                    sx={{ color: "#a9baca", height: "2.2rem" }}
                  />
                </IconButton>
              )}
              <IconButton onClick={() => setDeletePopup(true)}>
                <DeleteOutlineIcon
                  sx={{ color: "#a9baca", height: "2.2rem" }}
                />
              </IconButton>
            </>
          )}
          {toggleSidebar != null && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={toggleSidebar}
            >
              <CloseIcon sx={{ color: "#a9baca", height: "2.2rem" }} />
            </IconButton>
          )}
        </Stack>
      </Stack>
    );
  };

  const BookmarkOptions = () => {
    const getBookmarkSxProps = (option) => ({
      fontStyle: "italic",
      cursor: "pointer",
      textDecoration: selectedBookmarkOption === option ? "underline" : "none",
      textDecorationThickness: "2px",
      textUnderlineOffset: "8px",
      color:
        selectedBookmarkOption === option
          ? theme.palette.primary.main
          : "#696b6c",
    });

    return (
      <Stack mt={2} mb="8px" direction="row" justifyContent="center" gap={3}>
        <Typography
          variant="h6"
          sx={getBookmarkSxProps("notes")}
          onClick={() => {
            handleSelectBookmarkOption("notes");
          }}
        >
          Notes
        </Typography>
        <Typography
          variant="h6"
          sx={getBookmarkSxProps("citation")}
          onClick={() => handleSelectBookmarkOption("citation")}
        >
          Citation
        </Typography>
        {/* <Typography
          variant="h6"
          sx={getBookmarkSxProps("summary")}
          onClick={() => handleSelectBookmarkOption("summary")}
        >
          Summary
        </Typography> */}
        <Typography
          variant="h6"
          sx={getBookmarkSxProps("readinglevel")}
          onClick={() => handleSelectBookmarkOption("readinglevel")}
        >
          Reading Levels
        </Typography>
      </Stack>
    );
  };

  useEffect(() => {
    if (collectionId && collectionSourceId && collections.length) {
      setSelectedCollection(collectionId);
    }
  }, [collectionId, collections]);

  // useEffect(() => {
  //   const iframe = document.createElement("iframe");
  //   iframe.src = summaryInfoData.url;
  //   iframe.style =
  //     "border: none !important; margin: 0px !important; padding: 0px !important; width: 1px !important; min-width: 100% !important; overflow: hidden !important; display: block !important; visibility: hidden !important; position: fixed !important; height: 1px !important; pointer-events: none !important; user-select: none !important;";

  //   document.body.appendChild(iframe);

  //   return () => {
  //     document.body.removeChild(iframe);
  //   };
  // }, [summaryInfoData]);

  return (
    <Box sx={{ backgroundColor: navbarBackground }}>
      <TopHeader />
      {!edit && (
        <React.Fragment>
          <Stack px={5} direction="column" gap={1}>
            <Tooltip title={summaryInfoData.title}>
              <Typography noWrap={true} variant="body4">
                {summaryInfoData.title}
              </Typography>
            </Tooltip>
            <Tooltip title={summaryInfoData.url}>
              <SearchResultLink
                url={summaryInfoData.url}
                trackingCat="click_resultset_title"
                trackingOptions={{
                  url: summaryInfoData.url,
                  query:
                    currentSearchTerm != null &&
                    currentSearchTerm.trim().length > 0
                      ? currentSearchTerm
                      : undefined,
                  entity: summaryInfoData.publisher,
                  category:
                    currentCategory != null && currentCategory.trim().length > 0
                      ? currentCategory
                      : undefined,
                  type: "bookmark sidebar",
                  title: summaryInfoData.title,
                  date_published: summaryInfoData.utc_date_published
                }}
                variant="body3"
                color="grey"
                noWrap
                sx={{
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                  cursor: "pointer",
                }}
              >
                {summaryInfoData.url}
              </SearchResultLink>
            </Tooltip>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography
                variant="body2"
                color={theme.palette.grey[700]}
                fontWeight="bold"
              >
                {summaryInfoData.publisher}
              </Typography>
              <Typography variant="body2" color="grey">
                {getFormattedDate(summaryInfoData.published_date)}
              </Typography>
            </Stack>
            {create && (
              <TextField
                label="Enter new Collection name"
                value={bookmarkTitle}
                onChange={(e) => setBookmarkTitle(e.target.value)}
              ></TextField>
            )}
            <TextField
              select
              label={
                !create
                  ? "Add to Collection..."
                  : "Creating a sub-folder? Pick parent Collection here"
              }
              value={selectedCollection}
              onChange={handleSelect}
              variant="outlined"
              SelectProps={{
                open: dropdownOpen,
                onOpen: () => setDropdownOpen(true),
                onClose: () => setDropdownOpen(false),
                onClick: (event) => event.stopPropagation(),
              }}
              sx={{
                mt: 1.5,
              }}
            >
              {!create && (
                <MenuItem
                  key={0}
                  onClick={() => {
                    setBookmarkTitle("");
                    setCreate(true);
                  }}
                  value="create_new"
                >
                  + Create New
                </MenuItem>
              )}
              {create
                ? parentCollections.length &&
                  parentCollections.map((collection) => (
                    <MenuItem
                      key={collection.collectionId}
                      value={collection.collectionId}
                    >
                      {collection.name}
                    </MenuItem>
                  ))
                : collections.length &&
                  collections.map((collection) => (
                    <MenuItem
                      key={collection.collectionId}
                      value={collection.collectionId}
                    >
                      {collection.name}
                    </MenuItem>
                  ))}
            </TextField>
            {create && (
              <Box
                sx={{
                  backgroundColor: navbarBackground,
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <Button
                  onClick={handleSaveNewCollection}
                  disabled={disabled}
                  sx={{
                    mt: 2,
                    textTransform: "none",
                    color: "#fff",
                  }}
                >
                  Save
                </Button>
              </Box>
            )}
          </Stack>

          <BookmarkOptions />
        </React.Fragment>
      )}
    </Box>
  );
};

export default Header;
